import {createSelector} from 'reselect';

import type {SimpleProductFrameMSType} from 'constants/flow/flowTypesProduct';

import {getParentProduct, getActiveChildrenProductId} from 'selectors/product/product';
import {getCheckoutData} from 'selectors/checkout/checkout';
import type {ApplePayTotalsType} from 'reducers/applePayTotals/applePayTotalsType';
import type {CheckoutType} from 'reducers/checkout/checkoutType';
import {getApplePayTotals} from 'selectors/applePay/applePay';
import {getCartTotals} from 'selectors/cart/cart';

export type OrderDataType = {
    shipping: string,
    tax: string,
    coupon: string,
    revenue: string,
};

export type CombinedOrderDataType = {
    shipping: 'string',
    tax: 'string',
    coupon: 'string',
    revenue: 'string',
};

/**
 * Get item information as simple product.
 * @param {ApplicationStoreType} store - Application store.
 * @returns {SimpleProductFrameMSType} - Simple product.
 */
export const getSimpleProductInform = createSelector(
    getParentProduct,
    getActiveChildrenProductId,
    (product: SimpleProductFrameMSType, id: number): SimpleProductFrameMSType => {
        if (product.children && id in product.children) {
            return {...product, ...product.children[id]};
        }

        return product;
    },
);

/**
 * Get combined order data for apple pay.
 * @param {ApplicationStoreType} store - Application store.
 * @returns {CombinedOrderDataType} - Combined order data.
 */
export const getApplePayOrderData = createSelector(
    getCartTotals,
    getApplePayTotals,
    (
        {appliedCoupons: applePayCoupon = []}: {appliedCoupons: Array<string>},
        {shipping = '0', taxTotal: tax = '0', grandTotal = '0'}: ApplePayTotalsType,
    ) => ({
        shipping,
        tax,
        coupon: applePayCoupon.map(item => item.toUpperCase()).join(','),
        revenue: String(
            Math.max(0, Number(grandTotal) - Number(shipping) - Number(tax)).toFixed(2),
        ),
    }),
);

/**
 * Get combined order data V2.
 * @param {ApplicationStoreType} store - Application store.
 * @returns {CombinedOrderDataType} - Combined order data.
 */
export const getCheckoutOrderDataV2 = createSelector(
    getCartTotals,
    getCheckoutData,
    (
        {appliedCoupons = []}: {appliedCoupons: Array<string>},
        {
            totals: {shippingPrice: shipping = 0, totalTax: tax = 0, subtotal = 0, discount = 0},
        }: CheckoutType,
    ) => ({
        shipping: String(shipping),
        tax: String(tax),
        coupon: appliedCoupons.map(item => item.toUpperCase()).join(','),
        revenue: String(Math.max(0, Number(subtotal) - Number(discount)).toFixed(2)),
    }),
);
